import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { TrendingUp, ChefHat, Building2, Sparkles, ArrowRight, Download, Shield } from "lucide-react";

const $ = (n) => `$${(Math.round(n || 0)).toLocaleString("es-MX")}`;
const pct = (n) => `${(Number(n) || 0).toFixed(1)}%`;

const mesActualStr = () => new Date().toISOString().substring(0, 7);

const KPI = ({ label, value, hint, color = "#16a34a", testid }) => (
  <div className="eo-card p-4" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-stone-500">{label}</p>
    <p className="font-display text-2xl mt-1" style={{ color }}>{value}</p>
    {hint && <p className="text-[10px] mt-1 text-stone-500">{hint}</p>}
  </div>
);

// ============== BOCATTO · FOOD COST ==============
const ReporteBocatto = ({ mes }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/reportes/bocatto/food-cost`, { params: { mes } })
      .then(r => setData(r.data))
      .catch(e => toast.error("Error: " + (e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [mes]);
  if (loading) return <div className="p-6 text-stone-500 text-sm">Cargando food cost…</div>;
  if (!data) return null;
  const t = data.totales || {};
  const semaforo = (fc) => fc <= 28 ? "#16a34a" : fc <= 35 ? "#f59e0b" : "#dc2626";
  return (
    <div className="space-y-4" data-testid="reporte-bocatto">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Eventos" value={t.eventos} color="#ea580c" testid="bocatto-eventos" />
        <KPI label="Ingresos" value={$(t.ingresos)} color="#16a34a" testid="bocatto-ingresos" />
        <KPI label="Costo total" value={$(t.costo_total)} color="#dc2626" testid="bocatto-costo" />
        <KPI label="Food cost" value={pct(t.food_cost_pct)} hint={`Meta ≤ ${t.meta_food_cost_pct}%`} color={semaforo(t.food_cost_pct)} testid="bocatto-foodcost" />
        <KPI label="Utilidad estimada" value={$(t.utilidad)} color="#7c3aed" testid="bocatto-utilidad" />
      </div>
      <Card className="border-stone-200">
        <CardHeader className="pb-3"><CardTitle className="text-base">Detalle por evento</CardTitle></CardHeader>
        <CardContent>
          {data.eventos.length === 0 ? (
            <p className="text-sm text-stone-500 py-4 text-center">Sin eventos de Bocatto en este mes.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-stone-500 text-xs uppercase">
                    <th className="text-left py-2">Evento</th>
                    <th className="text-right py-2">Invitados</th>
                    <th className="text-right py-2">Ingresos</th>
                    <th className="text-right py-2">Costo</th>
                    <th className="text-right py-2">Food cost</th>
                    <th className="text-right py-2">Utilidad</th>
                  </tr>
                </thead>
                <tbody>
                  {data.eventos.map(e => (
                    <tr key={e.evento_id} className="border-b hover:bg-stone-50">
                      <td className="py-2">
                        <Link to={`/panel/eventos/${e.evento_id}`} className="font-medium text-stone-900 hover:text-emerald-700">{e.nombre}</Link>
                        <p className="text-xs text-stone-500">{e.fecha}</p>
                      </td>
                      <td className="text-right">{e.invitados}</td>
                      <td className="text-right">{$(e.ingresos)}</td>
                      <td className="text-right text-red-600">{$(e.costo_total)}</td>
                      <td className="text-right font-medium" style={{ color: semaforo(e.food_cost_pct) }}>{pct(e.food_cost_pct)}</td>
                      <td className="text-right font-medium text-emerald-700">{$(e.utilidad)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============== PETIT · OCUPACIÓN ==============
const ReportePetit = ({ mes }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/reportes/petit/ocupacion`, { params: { mes } })
      .then(r => setData(r.data))
      .catch(e => toast.error("Error: " + (e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [mes]);
  if (loading) return <div className="p-6 text-stone-500 text-sm">Cargando ocupación…</div>;
  if (!data) return null;
  const semaforo = (p) => p >= 60 ? "#16a34a" : p >= 40 ? "#f59e0b" : "#dc2626";
  return (
    <div className="space-y-4" data-testid="reporte-petit">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Días del mes" value={data.dias_del_mes} color="#16a34a" testid="petit-dias-mes" />
        <KPI label="Contratados" value={data.dias_contratados} color="#16a34a" testid="petit-dias-contratados" />
        <KPI label="Ocupación" value={pct(data.ocupacion_pct)} hint={`Meta ≥ ${data.meta_ocupacion_pct}%`} color={semaforo(data.ocupacion_pct)} testid="petit-ocupacion" />
        <KPI label="Ingresos mes" value={$(data.ingresos_mes)} color="#7c3aed" testid="petit-ingresos" />
        <KPI label="Ticket promedio" value={$(data.ticket_promedio)} color="#0ea5e9" testid="petit-ticket" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-stone-200">
          <CardHeader className="pb-3"><CardTitle className="text-base">Distribución de días</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <Barra label="Contratados" valor={data.dias_contratados} total={data.dias_del_mes} color="#16a34a" />
              <Barra label="Apartados" valor={data.dias_apartados} total={data.dias_del_mes} color="#fbbf24" />
              <Barra label="Bloqueados" valor={data.dias_bloqueados} total={data.dias_del_mes} color="#94a3b8" />
              <Barra label="Cancelados" valor={data.dias_cancelados} total={data.dias_del_mes} color="#dc2626" />
              <Barra label="Disponibles" valor={data.dias_disponibles} total={data.dias_del_mes} color="#cbd5e1" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-stone-200">
          <CardHeader className="pb-3"><CardTitle className="text-base">Eventos del mes</CardTitle></CardHeader>
          <CardContent>
            {data.eventos.length === 0 ? (
              <p className="text-sm text-stone-500 py-4 text-center">Sin eventos.</p>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {data.eventos.map(e => (
                  <Link key={e.evento_id} to={`/panel/eventos/${e.evento_id}`} className="flex items-center gap-2 p-2 rounded-lg hover:bg-stone-50">
                    <span className="text-xs text-stone-500 w-20">{e.fecha}</span>
                    <span className="flex-1 truncate text-sm text-stone-900">{e.nombre}</span>
                    <Badge variant="outline" className="text-[10px]">{e.estatus}</Badge>
                    <span className="text-sm font-medium text-emerald-700">{$(e.total)}</span>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Barra = ({ label, valor, total, color }) => {
  const pctv = total > 0 ? (valor / total) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1"><span className="text-stone-600">{label}</span><span className="font-medium text-stone-900">{valor} · {pctv.toFixed(0)}%</span></div>
      <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pctv}%`, background: color }} />
      </div>
    </div>
  );
};

// ============== KERMESITA · CONVERSIÓN ==============
const ReporteKermesita = ({ mes }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/reportes/kermesita/conversion`, { params: { mes } })
      .then(r => setData(r.data))
      .catch(e => toast.error("Error: " + (e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [mes]);
  if (loading) return <div className="p-6 text-stone-500 text-sm">Cargando conversión…</div>;
  if (!data) return null;
  const semaforo = (p) => p >= 35 ? "#16a34a" : p >= 20 ? "#f59e0b" : "#dc2626";
  return (
    <div className="space-y-4" data-testid="reporte-kermesita">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Cotizaciones" value={data.emitidas} color="#db2777" testid="kerm-emitidas" />
        <KPI label="Convertidas" value={data.convertidas} hint={`${data.perdidas} perdidas · ${data.en_proceso} en proceso`} color="#16a34a" testid="kerm-convertidas" />
        <KPI label="Tasa conversión" value={pct(data.tasa_conversion_pct)} hint={`Meta ≥ ${data.meta_conversion_pct}%`} color={semaforo(data.tasa_conversion_pct)} testid="kerm-tasa" />
        <KPI label="Monto cotizado" value={$(data.monto_emitido)} color="#0ea5e9" testid="kerm-monto-cot" />
        <KPI label="Ticket promedio" value={$(data.ticket_promedio)} color="#7c3aed" testid="kerm-ticket" />
      </div>
      <Card className="border-stone-200">
        <CardHeader className="pb-3"><CardTitle className="text-base">Cotizaciones de Kermesita</CardTitle></CardHeader>
        <CardContent>
          {data.cotizaciones.length === 0 ? (
            <p className="text-sm text-stone-500 py-4 text-center">Sin cotizaciones de Kermesita en este mes.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-stone-500 text-xs uppercase">
                    <th className="text-left py-2">Evento</th>
                    <th className="text-left py-2">Cotizado</th>
                    <th className="text-left py-2">Fecha evento</th>
                    <th className="text-right py-2">Monto</th>
                    <th className="text-center py-2">Estatus</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cotizaciones.map(c => (
                    <tr key={c.cotizacion_id} className="border-b hover:bg-stone-50">
                      <td className="py-2"><Link to={`/panel/eventos/${c.evento_id}`} className="font-medium text-stone-900 hover:text-pink-700">{c.nombre}</Link></td>
                      <td className="text-stone-500 text-xs">{c.fecha_cotizacion}</td>
                      <td className="text-stone-500 text-xs">{c.fecha_evento}</td>
                      <td className="text-right text-stone-900">{$(c.total_cotizado)}</td>
                      <td className="text-center"><Badge variant="outline" className="text-[10px]">{c.estatus_evento}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============== PÁGINA PRINCIPAL ==============
const TABS = [
  { key: "bocatto", label: "Bocatto · Food cost", icon: ChefHat, color: "#ea580c" },
  { key: "petit", label: "Petit · Ocupación", icon: Building2, color: "#16a34a" },
  { key: "kermesita", label: "Kermesita · Conversión", icon: Sparkles, color: "#db2777" },
];

const ReportesUnidades = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("bocatto");
  const [mes, setMes] = useState(mesActualStr());
  const esCelebraGroup = !!user?.es_celebra_group || user?.role === "superadmin";

  if (!esCelebraGroup) {
    return (
      <div className="p-8 max-w-xl mx-auto" data-testid="page-reportes-no-access">
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-6 text-center">
            <Shield className="w-10 h-10 mx-auto text-amber-700 mb-3" />
            <h2 className="font-display text-xl text-stone-900 mb-1">Acceso restringido</h2>
            <p className="text-sm text-stone-700">Esta sección está disponible únicamente para usuarios de Celebra Group.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="page-reportes-unidades">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-stone-900 flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-emerald-700" /> Reportes por unidad
          </h1>
          <p className="text-sm text-stone-600 mt-1">KPIs operativos de cada unidad de Celebra Group.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-wide text-stone-500">Mes</label>
          <Input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="w-44 border-stone-300" data-testid="input-mes-reporte" />
        </div>
      </div>

      <div className="flex gap-2 border-b border-stone-200">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 flex items-center gap-2 text-sm font-medium border-b-2 transition ${active ? "border-current" : "border-transparent text-stone-500 hover:text-stone-900"}`}
              style={{ color: active ? t.color : undefined }}
              data-testid={`tab-${t.key}`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{t.label}</span>
              <span className="sm:hidden">{t.label.split("·")[0].trim()}</span>
            </button>
          );
        })}
      </div>

      {tab === "bocatto" && <ReporteBocatto mes={mes} />}
      {tab === "petit" && <ReportePetit mes={mes} />}
      {tab === "kermesita" && <ReporteKermesita mes={mes} />}
    </div>
  );
};

export default ReportesUnidades;
